import React from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { DateRangeOutlined } from '@material-ui/icons';
import validateDateAndFormat from 'utils/validateDateAndFormat';
import { useDateFieldStyles } from 'Clients/ACCB/Components/DatePickerACCB';

// Narrowest a field can get before a label like "Uploaded From" runs under
// the calendar icon.
const FIELD_MIN_WIDTH = 180;
const FIELD_GAP = 15;

// Desktop keeps the two fields side by side, as before. Below md (the
// dashboard's mobile layout) they stack once there is no room for both, and
// the range asks a wrapping filter bar for a row wide enough for the pair
// rather than being squeezed beside the other filters.
const useRangeStyles = makeStyles((theme) => ({
  range: {
    display: 'flex',
    gap: `${FIELD_GAP}px`,
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      flex: `1 1 ${FIELD_MIN_WIDTH * 2 + FIELD_GAP}px`,
    },
  },
  field: {
    [theme.breakpoints.down('sm')]: {
      flex: `1 1 ${FIELD_MIN_WIDTH}px`,
    },
  },
}));

const DEFAULT_MIN_DATE = new Date(1900, 0, 1);
const DEFAULT_MAX_DATE = new Date(2100, 0, 1);

const getStartOfDay = (d) => {
  if (!d) return null;
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

export default function DateRangePickerACCB({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  startLabel = 'From',
  endLabel = 'To',
  minDate = DEFAULT_MIN_DATE,
  maxDate = DEFAULT_MAX_DATE,
  required = false,
}) {
  const classes = useDateFieldStyles();
  const rangeClasses = useRangeStyles();
  const startDateObj = getStartOfDay(startDate);
  const endDateObj = getStartOfDay(endDate);

  const effectiveMaxDateForStart = endDateObj || maxDate;
  const effectiveMinDateForEnd = startDateObj || minDate;

  const startMinMessage = `Date cannot be before ${validateDateAndFormat(minDate, 'dd/MM/yyyy')}`;
  const startMaxMessage = endDateObj
    ? `${startLabel} date must be equal to or less than ${endLabel} date`
    : `Date cannot be after ${validateDateAndFormat(maxDate, 'dd/MM/yyyy')}`;

  const endMinMessage = startDateObj
    ? `${endLabel} date must be equal to or greater than ${startLabel} date`
    : `Date cannot be before ${validateDateAndFormat(minDate, 'dd/MM/yyyy')}`;
  const endMaxMessage = `Date cannot be after ${validateDateAndFormat(maxDate, 'dd/MM/yyyy')}`;

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Box className={rangeClasses.range}>
        <DatePicker
          views={['year', 'month', 'date']}
          inputVariant="outlined"
          size="small"
          fullWidth
          className={rangeClasses.field}
          InputLabelProps={{ className: classes.label }}
          label={startLabel}
          placeholder="DD/MM/YYYY"
          format="dd/MM/yyyy"
          required={required}
          value={startDate}
          onChange={setStartDate}
          minDate={minDate}
          maxDate={effectiveMaxDateForStart}
          minDateMessage={startMinMessage}
          maxDateMessage={startMaxMessage}
          InputProps={{
            endAdornment: <DateRangeOutlined style={{ cursor: 'pointer' }} />,
          }}
          clearable
          clearLabel="Clear"
        />
        <DatePicker
          views={['year', 'month', 'date']}
          inputVariant="outlined"
          size="small"
          fullWidth
          className={rangeClasses.field}
          InputLabelProps={{ className: classes.label }}
          label={endLabel}
          placeholder="DD/MM/YYYY"
          format="dd/MM/yyyy"
          required={required}
          value={endDate}
          onChange={setEndDate}
          minDate={effectiveMinDateForEnd}
          maxDate={maxDate}
          minDateMessage={endMinMessage}
          maxDateMessage={endMaxMessage}
          InputProps={{
            endAdornment: <DateRangeOutlined style={{ cursor: 'pointer' }} />,
          }}
          clearable
          clearLabel="Clear"
        />
      </Box>
    </MuiPickersUtilsProvider>
  );
}
